import React from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Checkbox, Combobox, Icon, Input } from '@node-space/ui-components'
import { useBankDetailsForm } from 'pages/Contacts/hooks/useBankDetailsForm'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { AddressDetails } from './AddressDetails'

type BankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  contactBeneficiary?: BeneficiaryDetails
}

export const BankDetails = ({
  formFieldLabels,
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
  trigger,
  setError,
  contactBeneficiary,
}: BankDetailsProps) => {
  const { t } = useTranslation()

  const {
    // Currency/Transfer flags
    isUsd,
    isZar,
    isInternational,
    isLocal,
    isRoutingNumberVisible,
    isAccountNumberAndBankCodeInline,
    isAccountTypeRequired,

    // Validation states
    isValidAccountNumber,
    isValidBankCode,
    isValidRoutingNumber,

    // Loading states
    isBankDetailsValidationLoading,
    isValidatingRoutingNumber,
    isValidatingScan,
    isValidatingBic,
    countriesLoading,
    isFetchingSouthAfricanBanks,

    // Error states
    isSouthAfricanBanksError,

    // Options
    accountTypeOptions,
    southAfricanBankOptions,
    internationalCountryList,

    // Handlers
    onFieldUpdate,
    onAccountTypeChange,
    handleAccountNumberOnBlur,
    handleBankCodeOnBlur,
    handleAccountNumberConfirmOnBlur,
    handleInternationalRoutingValidation,
    onInternationalCountrySelection,
    handleBankOnChange,
  } = useBankDetailsForm({
    formValues,
    errors,
    clearErrors,
    setValue,
    trigger,
    setError,
    contactBeneficiary,
  })

  const { accountNumber, bankCode, bankCountryCode } = formValues || {}

  const handleIntermediaryBankDetailsChange = (checked: boolean): void => {
    setValue('isIntermediaryBankDetailsRequired', checked)
  }

  return (
    <Box width="full" paddingT={12}>
      <h2 className="text-foreground-prominent text-h2">{t('formFields.bankDetails')}</h2>
      <Box flexCol width="full" gapY={16} paddingT={12}>
        {/* USD International: Country selection first */}
        {isUsd && isInternational && (
          <Box>
            <Combobox
              {...register('bankCountryCode')}
              key={formValues?.bankCountryCode}
              label={t('country')}
              id={t('bankCountryCode')}
              selectPlaceholder={t('searchOrSelectACountry')}
              items={internationalCountryList}
              onChange={onInternationalCountrySelection}
              loading={countriesLoading}
              defaultValue={formValues?.bankCountryCode}
              errorText={errors?.bankCountryCode?.message}
            />
          </Box>
        )}

        {/* Main bank details section */}
        {(isLocal || (isInternational && (!isUsd || bankCountryCode))) && (
          <Box
            gapX={12}
            gapY={16}
            paddingB={12}
            width="full"
            flex={isAccountNumberAndBankCodeInline}
            flexCol={!isAccountNumberAndBankCodeInline}
          >
            {/* South African Banks (ZAR only) */}
            {isZar && isLocal && (
              <Box flexSize="fill">
                <Combobox
                  {...register('bankNameCode')}
                  label={t('bank')}
                  id="bankNameCode"
                  items={southAfricanBankOptions}
                  onChange={handleBankOnChange}
                  errorText={
                    errors?.bankNameCode?.message ??
                    (isSouthAfricanBanksError ? t('fiatSend.failedToLoadBanks') : undefined)
                  }
                  loading={isFetchingSouthAfricanBanks}
                  defaultValue={formValues?.bankNameCode}
                />
              </Box>
            )}

            {/* Account Number */}
            <Box
              {...(isAccountNumberAndBankCodeInline && {
                flexSize: 'auto',
                width: 'full',
                gapX: 12,
              })}
            >
              <Input
                {...register('accountNumber')}
                name="accountNumber"
                testId="accountNumber"
                label={formFieldLabels?.accountNumber}
                placeholder={formFieldLabels?.accountNumberPlaceholder}
                errorText={errors?.accountNumber?.message}
                error={!!errors?.accountNumber?.message}
                onBlur={handleAccountNumberOnBlur}
                onChange={onFieldUpdate('accountNumber')}
                loading={isBankDetailsValidationLoading}
                IconButton={
                  isValidAccountNumber &&
                  accountNumber && <Button variant="ghost" iconElement={<Icon name="Check" />} />
                }
                iconPosition="right"
              />
            </Box>

            {/* Account Number Confirm (USD local only) */}
            {isUsd && isLocal && (
              <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
                <Input
                  {...register('accountNumberConfirm')}
                  name="accountNumberConfirm"
                  testId="accountNumberConfirm"
                  label={t('formFields.confirmAccountNumber')}
                  placeholder={t('formFields.placeholder.confirmAccountNumber')}
                  errorText={errors?.accountNumberConfirm?.message}
                  error={!!errors?.accountNumberConfirm?.message}
                  onChange={onFieldUpdate('accountNumberConfirm')}
                  onBlur={handleAccountNumberConfirmOnBlur}
                />
              </Box>
            )}

            {/* Bank Code */}
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('bankCode')}
                name="bankCode"
                testId="bankCode"
                label={formFieldLabels.bankCode}
                placeholder={formFieldLabels?.bankCodePlaceholder}
                errorText={errors?.bankCode?.message}
                error={!!errors?.bankCode?.message}
                loading={isValidatingRoutingNumber || isValidatingScan || isValidatingBic}
                onChange={onFieldUpdate('bankCode')}
                onBlur={handleBankCodeOnBlur}
                IconButton={
                  isValidBankCode &&
                  bankCode && <Button variant="ghost" iconElement={<Icon name="Check" />} />
                }
                iconPosition="right"
              />
            </Box>

            {/* Account Type (USD or ZAR local only) */}
            {isAccountTypeRequired && isLocal && (
              <Combobox
                {...register('accountType')}
                key={formValues?.accountType}
                id="accountType"
                testId="accountType"
                label={t('formFields.accountType')}
                items={accountTypeOptions}
                errorText={errors?.accountType?.message}
                defaultValue={formValues?.accountType}
                onChange={onAccountTypeChange}
              />
            )}
          </Box>
        )}

        {/* Routing Number (USD international to US only) */}
        {isRoutingNumberVisible && (
          <Box>
            <Input
              {...register('routingNumber')}
              testId="routingNumber"
              label={t('formFields.routingNumber')}
              placeholder={t('formFields.placeholder.routingNumber')}
              errorText={errors?.routingNumber?.message}
              error={!!errors?.routingNumber?.message}
              loading={isValidatingRoutingNumber}
              onChange={onFieldUpdate('routingNumber')}
              onBlur={handleInternationalRoutingValidation}
              value={formValues?.routingNumber}
              IconButton={
                isValidRoutingNumber &&
                formValues?.routingNumber && (
                  <Button variant="ghost" iconElement={<Icon name="Check" />} />
                )
              }
              iconPosition="right"
            />
          </Box>
        )}

        {/* Bank Name (International only) */}
        {isInternational && (!isUsd || bankCountryCode) && (
          <Box>
            <Input
              {...register('bankName')}
              name="bankName"
              testId="bankName"
              label={t('beneficiarysBankName')}
              placeholder={t('enterBankName')}
              errorText={errors?.bankName?.message}
              error={!!errors?.bankName?.message}
              onChange={onFieldUpdate('bankName')}
            />
          </Box>
        )}

        {/* Address Details (International only) */}
        {isInternational && (!isUsd || bankCountryCode) && (
          <AddressDetails
            formValues={formValues}
            errors={errors}
            type={AddressTypes.BANK}
            clearErrors={clearErrors}
            register={register}
            setError={setError}
            setValue={setValue}
          />
        )}

        {/* Country selection at bottom (Non-USD international) */}
        {!isUsd && isInternational && (
          <Box>
            <Combobox
              {...register('bankCountryCode')}
              key={formValues?.bankCountryCode}
              label={t('country')}
              id={t('bankCountryCode')}
              selectPlaceholder={t('searchOrSelectACountry')}
              items={internationalCountryList}
              onChange={onInternationalCountrySelection}
              loading={countriesLoading}
              defaultValue={formValues?.bankCountryCode}
              errorText={errors?.bankCountryCode?.message}
            />
          </Box>
        )}
      </Box>

      {isInternational && (
        <Box paddingT={12}>
          <Checkbox
            id="beneficiary-address-copy-details-checkbox"
            name="beneficiary-address-copy-details-checkbox"
            testId="beneficiary-address-copy-details-checkbox"
            onCheckedChange={handleIntermediaryBankDetailsChange}
            label={t('contacts.intermediaryBankDetailsLabel')}
            checked={formValues?.isIntermediaryBankDetailsRequired}
          >
            {t('contacts.intermediaryBankDetailsDescription')}
          </Checkbox>
        </Box>
      )}
    </Box>
  )
}
